import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const ROLE_LABELS = {
  partner: "Managing Partner", advicer: "Immigration Adviser",
  paralegal: "Paralegal", caseworker: "Caseworker",
};

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ roles: [] });
  const [open, setOpen] = useState(false);
  const emp = { name: "", email: "", role: "paralegal", password: "" };
  const [f, setF] = useState(emp);
  const { user: current } = useAuth();

  const load = async () => {
    const [u, m] = await Promise.all([api.get("/users"), api.get("/meta")]);
    setUsers(u.data); setMeta(m.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/users", f); toast.success("Team member added"); setOpen(false); setF(emp); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this team member?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1">Chambers</div>
          <h2 className="font-serif-brand text-3xl">Team</h2>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-user-button" className="rounded-sm bg-[#0B1528] hover:bg-[#1E293B] h-9"><Plus size={14} className="mr-1" /> New Member</Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm max-w-md">
            <DialogHeader><DialogTitle>New team member</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Full name</Label>
                <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 rounded-sm" data-testid="user-name" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
                <Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1 rounded-sm" data-testid="user-email" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Role</Label>
                <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-sm bg-white">
                    {(meta.roles || []).map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Initial password</Label>
                <Input required type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="mt-1 rounded-sm" data-testid="user-password" />
                <div className="text-xs text-slate-500 mt-1">Share this with the member; they should change it on first sign in.</div>
              </div>
              <DialogFooter><Button data-testid="save-user" type="submit" className="rounded-sm bg-[#0B1528] hover:bg-[#1E293B]">Create member</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-slate-200 bg-white rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-6 py-3 font-medium">Name</th>
              <th className="text-left px-6 py-3 font-medium">Email</th>
              <th className="text-left px-6 py-3 font-medium">Role</th>
              <th className="text-left px-6 py-3 font-medium">Joined</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {users.map((u) => (
              <tr key={u.id} className="hover:bg-slate-50">
                <td className="px-6 py-3 font-medium">{u.name}</td>
                <td className="px-6 py-3 text-slate-600">{u.email}</td>
                <td className="px-6 py-3 text-slate-600">{ROLE_LABELS[u.role] || u.role}</td>
                <td className="px-6 py-3 text-slate-500">{formatDate(u.created_at)}</td>
                <td className="px-6 py-3 text-right">
                  {u.id !== current?.id && (
                    <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u.id)} className="text-slate-400 hover:text-red-700"><Trash size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
